/**
 * Zana Bundle Selector
 * - Switches the product form variant id (real Shopify prices)
 * - Updates main price + compare-at + SAVE % for one selected bundle
 * - Quantity (+/−) controls how many of that bundle variant to add
 * - Initial default: desktop = middle option, mobile = first option
 * - Manual selection is never overridden by resize
 */
if (!customElements.get('zana-bundle-selector')) {
  customElements.define(
    'zana-bundle-selector',
    class ZanaBundleSelector extends HTMLElement {
      connectedCallback() {
        this.cards = Array.from(this.querySelectorAll('[data-bundle-card]'));
        if (!this.cards.length) return;

        this.formId = this.dataset.productFormId;
        this.baseUnitPrice = parseInt(this.dataset.baseUnitPrice || '0', 10);
        this.roundSave = this.dataset.roundSave === 'true';
        this.desktopMin = parseInt(this.dataset.desktopMin || '750', 10);
        this.defaultDesktop = Math.max(1, parseInt(this.dataset.defaultDesktop || '2', 10));
        this.defaultMobile = Math.max(1, parseInt(this.dataset.defaultMobile || '1', 10));
        this.userHasSelected = false;
        this.hasAppliedInitialDefault = false;
        this.priceRoots = Array.from(
          document.querySelectorAll(`[data-zana-bundle-price="${this.dataset.sectionId}"]`)
        );

        this.cards.forEach((card) => {
          card.addEventListener('click', (event) => {
            event.preventDefault();
            if (card.disabled || card.classList.contains('is-unavailable')) return;
            this.userHasSelected = true;
            this.selectCard(card, { updateHistory: true });
          });
        });

        this.initWhenReady();
      }

      isDesktopViewport() {
        return window.matchMedia(`(min-width: ${this.desktopMin}px)`).matches;
      }

      getInitialCard() {
        const params = new URLSearchParams(window.location.search);
        const urlVariant = params.get('variant');
        if (urlVariant) {
          const fromUrl = this.cards.find((card) => card.dataset.variantId === urlVariant);
          if (fromUrl && !fromUrl.disabled) {
            this.userHasSelected = true;
            return fromUrl;
          }
        }

        const optionIndex = this.isDesktopViewport() ? this.defaultDesktop : this.defaultMobile;
        const byIndex = this.cards.find(
          (card) => parseInt(card.dataset.optionIndex || '0', 10) === optionIndex
        );
        if (byIndex && !byIndex.disabled) return byIndex;

        return this.cards.find((card) => !card.disabled) || this.cards[0];
      }

      initWhenReady(attempt = 0) {
        const form = this.getProductForm();
        if (form || attempt > 40) {
          if (!this.hasAppliedInitialDefault) {
            this.hasAppliedInitialDefault = true;
            const preselected = this.getInitialCard();
            if (preselected) this.selectCard(preselected, { updateHistory: false });
          }
          return;
        }
        requestAnimationFrame(() => this.initWhenReady(attempt + 1));
      }

      getProductForm() {
        if (this.formId) {
          const form = document.getElementById(this.formId);
          if (form) return form;
        }
        return (
          this.closest('product-info')?.querySelector('product-form form') ||
          document.querySelector('product-form form[data-type="add-to-cart-form"]')
        );
      }

      /** Main product form + sticky cart alt form only (never other product ATCs on the page). */
      getRelatedForms() {
        const forms = [];
        const main = this.getProductForm();
        if (main) forms.push(main);
        if (this.formId) {
          const sticky = document.getElementById(`${this.formId}--alt`);
          if (sticky && sticky !== main) forms.push(sticky);
        }
        return forms;
      }

      setVariantIdOnForms(variantId) {
        this.getRelatedForms().forEach((form) => {
          const idInput = form.querySelector('input[name="id"]');
          if (!idInput) return;
          idInput.value = variantId;
          idInput.disabled = false;
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      formatMoney(cents) {
        const value = Math.max(0, Math.round(Number(cents) || 0));
        if (window.theme?.Currency?.formatMoney && theme.shopSettings?.moneyFormat) {
          return theme.Currency.formatMoney(value, theme.shopSettings.moneyFormat);
        }
        return (value / 100).toFixed(2);
      }

      selectCard(card, { updateHistory = true } = {}) {
        const variantId = card.dataset.variantId;
        if (!variantId) return;

        this.cards.forEach((item) => {
          const selected = item === card;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-checked', selected ? 'true' : 'false');
        });

        // Sync main + sticky cart forms only. Do not reset quantity.
        this.setVariantIdOnForms(variantId);
        this.updatePrice(card);

        if (updateHistory && typeof history !== 'undefined') {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variantId);
          window.history.replaceState({}, '', url.toString());
        }

        this.dispatchEvent(
          new CustomEvent('zana-bundle:change', {
            bubbles: true,
            detail: {
              variantId,
              price: card.dataset.price,
              multiplier: card.dataset.compareMultiplier,
            },
          })
        );
      }

      updatePrice(card) {
        if (!this.priceRoots?.length) {
          this.priceRoots = Array.from(
            document.querySelectorAll(`[data-zana-bundle-price="${this.dataset.sectionId}"]`)
          );
        }
        if (!this.priceRoots.length) return;

        const price = parseInt(card.dataset.price || '0', 10);
        const multiplier = Math.max(1, parseInt(card.dataset.compareMultiplier || '1', 10));
        const compare = this.baseUnitPrice > 0 ? this.baseUnitPrice * multiplier : 0;
        const showCompare = compare > price;
        const rawSave = showCompare ? ((compare - price) * 100) / compare : 0;
        const savePercent = showCompare ? Math.round(rawSave) : 0;

        const priceFormatted = card.dataset.priceFormatted || this.formatMoney(price);
        const compareFormatted = showCompare ? this.formatMoney(compare) : '';
        const saveText = savePercent > 0 ? `SAVE ${savePercent}%` : '';

        this.priceRoots.forEach((priceRoot) => {
          const current = priceRoot.querySelector('[data-zana-bundle-price-current]');
          const compareEl = priceRoot.querySelector('[data-zana-bundle-price-compare]');
          const saveEl = priceRoot.querySelector('[data-zana-bundle-price-save]');

          if (current) current.textContent = priceFormatted;

          if (compareEl) {
            compareEl.textContent = compareFormatted;
            compareEl.hidden = !showCompare;
          }

          if (saveEl) {
            saveEl.textContent = saveText;
            saveEl.hidden = !saveText;
          }
        });
      }
    }
  );
}
